import { useEffect, useMemo, useState } from 'react'

import {
  addDays,
  addMonths,
  addWeeks,
  getDaysInMonth,
  isBefore,
  isPast,
  isSameDay,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subWeeks,
} from 'date-fns'

import { useDateUtils } from 'hooks/useDateUtils'
import { useIsMobile } from 'hooks/useIsMobile'
import { CalendarMarker, CalendarRange } from 'interfaces/types'

import {
  StyledCalendar,
  StyledCalendarDateGrid,
  StyledCalendarDayLabel,
  StyledCalendarDayNumber,
  StyledCalendarDayNumberWrapper,
  StyledCalendarDaysOfWeek,
  StyledCalendarDaysWrapper,
  StyledCalendarMarkerIcon,
  StyledCalendarMonthIndicator,
  StyledCalendarMonths,
  StyledCalendarMonthWrapper,
  StyledCalendarNavArrow,
  StyledCalendarToaster,
  StyledCalendarToasterCloseButton,
  StyledCalendarToasterIcon,
  StyledCalendarTooltip,
  StyledCalendarTooltipContent,
  StyledCalendarTooltipIcon,
} from './index.styled'

export type CalendarMode = 'weekly' | 'monthly' | 'yearly'

export type CalendarProps = {
  mode: CalendarMode
  start?: Date
  from?: Date
  selected?: Date | null
  onChangeStart?: (d: Date) => void
  onChangeSelected?: (d: Date) => void
  ranges?: CalendarRange[]
  markers?: CalendarMarker[]
  className?: string
  renderTooltip?: (d: Date) => JSX.Element
}

export function Calendar({
  mode,
  start = new Date(),
  selected = null,
  onChangeStart,
  onChangeSelected,
  ranges = [],
  markers = [],
  className,
  renderTooltip,
  from,
}: CalendarProps) {
  const { localize } = useDateUtils()
  const isMobile = useIsMobile()

  const [isToasterVisible, setIsToasterVisible] = useState(
    () => mode === 'yearly' && !!renderTooltip,
  )
  const [hoverDay, setHoverDay] = useState<Date | null>(null)

  useEffect(() => {
    if (selected && from) {
      if (isBefore(selected, from)) {
        if (onChangeSelected) {
          onChangeSelected(from)
        }
        if (onChangeStart) {
          onChangeStart(from)
        }
      }
    }
    // eslint-disable-next-line
  }, [selected, from])

  const weekDays = useMemo(() => {
    if (!localize) {
      return []
    }
    return [1, 2, 3, 4, 5, 6, 0].map((i) =>
      localize.day(i, { width: 'narrow' }),
    )
  }, [localize])

  const startDate = useMemo(() => {
    if (mode === 'weekly') {
      return addDays(startOfWeek(start), 1)
    } else if (mode === 'monthly') {
      return startOfMonth(start)
    } else {
      return startOfYear(start)
    }
  }, [start, mode])

  function navigatePrev() {
    if (onChangeStart) {
      if (mode === 'weekly') {
        onChangeStart(subWeeks(startDate, 1))
      } else if (mode === 'monthly') {
        onChangeStart(subMonths(startDate, 1))
      }
    }
  }

  function navigateNext() {
    if (onChangeStart) {
      if (mode === 'weekly') {
        onChangeStart(addWeeks(startDate, 1))
      } else if (mode === 'monthly') {
        onChangeStart(addMonths(startDate, 1))
      }
    }
  }

  function handleClickDay(day: Date) {
    if (from && isBefore(day, from)) {
      return
    }
    if (!isToasterVisible) {
      setIsToasterVisible(true)
    }
    if (onChangeSelected) {
      onChangeSelected(day)
    }
  }

  function renderWeekDays() {
    return (
      <StyledCalendarDaysOfWeek>
        {weekDays.map((d, i) => (
          <StyledCalendarDayLabel key={`${d}-${i}`}>{d}</StyledCalendarDayLabel>
        ))}
      </StyledCalendarDaysOfWeek>
    )
  }

  function renderWeek() {
    const days: Date[] = []
    if (mode === 'weekly') {
      for (let i = 0; i < 7; i++) {
        days.push(addDays(startDate, i))
      }
    }
    return (
      <StyledCalendarDaysWrapper>
        {renderWeekDays()}
        <StyledCalendarDateGrid>{days.map(renderDay)}</StyledCalendarDateGrid>
      </StyledCalendarDaysWrapper>
    )
  }

  function renderMonth(monthIndex: number, year = startDate.getFullYear()) {
    if (!localize) {
      return null
    }
    const daysInMonth = getDaysInMonth(new Date(year, monthIndex))
    const days: (Date | null)[] = []
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, monthIndex, i))
    }
    const weekDayIndex = days[0] ? days[0].getDay() : 0
    if (weekDayIndex !== 1) {
      const idx = weekDayIndex === 0 ? 6 : weekDayIndex - 1
      for (let i = 0; i < idx; i++) {
        days.unshift(null)
      }
    }
    return (
      <StyledCalendarMonthWrapper
        key={`month-${monthIndex}`}
        data-month-index={monthIndex}
      >
        <StyledCalendarMonthIndicator>
          {localize.month(monthIndex)}
        </StyledCalendarMonthIndicator>
        <StyledCalendarDaysWrapper>
          {mode === 'monthly' && renderWeekDays()}
          <StyledCalendarDateGrid>{days.map(renderDay)}</StyledCalendarDateGrid>
        </StyledCalendarDaysWrapper>
      </StyledCalendarMonthWrapper>
    )
  }

  function renderYear() {
    return Array(12)
      .fill(undefined)
      .map((_, monthIndex) => renderMonth(monthIndex))
  }

  function renderMonths() {
    const nextMonth = addMonths(startDate, 1)
    return (
      <StyledCalendarMonths>
        {renderMonth(startDate.getMonth(), startDate.getFullYear())}
        {renderMonth(nextMonth.getMonth(), nextMonth.getFullYear())}
      </StyledCalendarMonths>
    )
  }

  function renderDay(d: Date | null, index: number) {
    if (d === null) {
      return <StyledCalendarDayNumberWrapper key={`idx-${index}`} />
    }
    const day = d.getDate()
    const isSelected = selected ? isSameDay(d, selected) : false
    const range = ranges.find((r) =>
      isWithinInterval(d, { start: r.startDate, end: r.endDate }),
    )
    const isDateToday = isToday(d)
    const isRangeFirstDay = range?.startDate
      ? isSameDay(d, range.startDate)
      : false
    const isRangeLastDay = range?.endDate ? isSameDay(d, range.endDate) : false
    const marker = markers.find((m) => isSameDay(m.date, d))
    const isHover = hoverDay ? isSameDay(d, hoverDay) : false
    return (
      <StyledCalendarDayNumberWrapper
        key={day}
        data-is-today={isDateToday}
        data-is-past={!isDateToday && isPast(d)}
        data-is-selected={isSelected}
        data-is-range={!!range}
        data-is-range-first={isRangeFirstDay}
        data-is-range-last={isRangeLastDay}
        range={range}
        onMouseEnter={() => {
          if (from && isBefore(d, from)) {
            return
          }
          setHoverDay(d)
        }}
        onMouseLeave={() => setHoverDay(null)}
      >
        <StyledCalendarDayNumber onClick={() => handleClickDay(d)}>
          {day}
          {!!marker && (
            <StyledCalendarMarkerIcon
              color={
                isDateToday && isSelected ? 'white' : marker && marker.color
              }
            />
          )}
          <div>
            {mode === 'monthly' && renderTooltip && isHover && (
              <StyledCalendarTooltip>
                <StyledCalendarTooltipIcon>
                  {renderInfoIcon()}
                </StyledCalendarTooltipIcon>
                <StyledCalendarTooltipContent>
                  {renderTooltip(d)}
                </StyledCalendarTooltipContent>
              </StyledCalendarTooltip>
            )}
          </div>
        </StyledCalendarDayNumber>
      </StyledCalendarDayNumberWrapper>
    )
  }

  function renderInfoIcon() {
    return <img loading="lazy" src="/svg/info-light.svg" alt="" />
  }

  return (
    <StyledCalendar
      className={className}
      data-cal-mode={mode}
      data-toaster-visible={isToasterVisible}
    >
      {mode === 'yearly' && renderWeekDays()}
      {(mode === 'weekly' || mode === 'monthly') && (
        <>
          <StyledCalendarNavArrow
            className="cal-nav-prev"
            src={
              isMobile
                ? '/svg/chevron-left-gray.svg'
                : '/svg/long-gray-left-arrow.svg'
            }
            width={16}
            height={16}
            onClick={navigatePrev}
          />
          <StyledCalendarNavArrow
            className="cal-nav-next"
            src={
              isMobile
                ? '/svg/chevron-left-gray.svg'
                : '/svg/long-gray-left-arrow.svg'
            }
            width={16}
            height={16}
            onClick={navigateNext}
          />
        </>
      )}
      {mode === 'weekly' && renderWeek()}
      {mode === 'yearly' && renderYear()}
      {mode === 'monthly' && renderMonths()}
      <div>
        {mode === 'yearly' && renderTooltip && selected && isToasterVisible && (
          <StyledCalendarToaster>
            <StyledCalendarToasterIcon>
              {renderInfoIcon()}
            </StyledCalendarToasterIcon>
            <StyledCalendarTooltipContent>
              {renderTooltip(selected)}
            </StyledCalendarTooltipContent>
            <StyledCalendarToasterCloseButton
              src="/images/close.svg"
              onClick={() => setIsToasterVisible(false)}
            />
          </StyledCalendarToaster>
        )}
      </div>
    </StyledCalendar>
  )
}
